<template>
    <AuthLayout :imageSrc="currentImage" :layoutType="layoutType">
        <div class="flex justify-content-center align-items-center w-full">
            <Card class="motivation-card" :style="step === 3 ? 'max-width: 80%' : ''">
                <template #content>
                    <div class="flex flex-column align-items-center">
                        <CustomStepper v-if="step > 3 && step < 9" :current-step="getStepperStep(step)"
                            :steps="stepperSteps" class="mb-6" />
                    </div>
                    <div class="flex flex-column align-items-center justify-content-center w-full">
                        <div class="mb-4" v-if="step !== 1">
                            <h2 class="Paragraph-Medium mb-2 text-center">{{ Title }}</h2>
                            <p class="Caption text-center">{{ Subtitle }}</p>
                            <p class="Caption text-center">{{ regularSubtitle }}</p>
                        </div>
                        <RegistrationView v-if="step === 1" @registration-success="setRegistrationData" />
                        <FirstOnboardingView v-if="step === 2" @get-started="setGetStarted" />
                        <PaymentPlansView v-if="step === 3" @package-selected="setPackageInfo" @previous-step="previousStep" />                       
                        <SecondOnboardingView v-if="step === 4" @submit-personal-info="setPersonalInfo"
                            @previous-step="previousStep" />
                        <ThirdOnboardingView v-if="step === 5" @submit-teaching-info="setTeachingInfo"
                            @previous-step="previousStep" />
                        <FourthOnboardingFlow v-if="step === 6" @previous-step="previousStep"
                            @submit-experiance-info="setExperianceInfo" />
                        <FifthOnboardingFlow v-if="step === 7" @previous-step="previousStep"
                            @submit-verification-info="setVerificationInfo" />
                        <SixthOnboardingFlow v-if="step === 8" @previous-step="previousStep"
                            @submit-for-verification="(data) => doVerification(data)" />
                        <SeventhOnboardingFlow v-if="step === 9" @bypass-verification="bypassVerification" />
                    </div>
                </template>
            </Card>
        </div>
    </AuthLayout>
</template>

<script setup>
import { ref, defineProps, watch, onMounted } from 'vue';
import AuthLayout from "@/layout/AuthLayout.vue";
import RegistrationView from "@/components/RegistrationComponent.vue"
import FirstOnboardingView from "@/components/tutorOnboardingFlow/tutorOnboardingFirstPage.vue"
import SecondOnboardingView from "@/components/tutorOnboardingFlow/tutorOnboardingSecondPage.vue"
import ThirdOnboardingView from "@/components/tutorOnboardingFlow/tutorOnboardingThirdPage.vue"
import FourthOnboardingFlow from "@/components/tutorOnboardingFlow/tutorOnboardingFourthPage.vue"
import SixthOnboardingFlow from "@/components/tutorOnboardingFlow/tutorOnboardingSixthPage.vue"
import FifthOnboardingFlow from "@/components/tutorOnboardingFlow/tutorOnboardingFifthPage.vue"
import SeventhOnboardingFlow from "@/components/tutorOnboardingFlow/tutorOnboardingSeventhPage.vue"
import PaymentPlansView from '@/components/tutorOnboardingFlow/tutorOnboardingPaymentPlansPage.vue';
import step1Img from "@/assets/Images/image1.png";
import step2Img from "@/assets/Images/stdOnboarding4.png";
import { notifyContrast } from '@/utils/notification';
import { updateProfile, getProfile } from '@/api/tutor';
import { useAuthStore } from "@/stores/authStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { computed } from 'vue'
import Card from 'primevue/card';
import CustomStepper from "@/components/CustomStepper.vue";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();

// Institute-sponsored tutors already have a plan and must not see the package step (step 3).
const managedByInstitute = computed(() => subscriptionStore.managedByInstitute);

const props = defineProps({
    registrationData: {
        type: Object,
        required: false,
    }
});
const router = useRouter();
const localData = ref(props.registrationData);
const step = ref(1);
const alreadyLoggedIn = ref(false);
const Title = computed(() => stepContent[step.value]?.title || '')
const Subtitle = computed(() => stepContent[step.value]?.subtitle || '')
const regularSubtitle = computed(() => stepContent[step.value]?.regularSubtitle || '')
const username = (authStore.user?.username || "").split(' ')[0];
const stepContent = {
    2: {
        title: `Hey ${username}. Ready to be a verified tutor?`,
        subtitle: "There are few more steps to be a verified tutor."
    },
    3: {
        title: `Hey ${username}, Let's start the journey by choosing a package`,
        subtitle: "Let's choose a package that best fits for your need, and anytime you can upgrade or downgrade your package."
    },
    4: {
        title: "Personal Information",
        subtitle: "Provide your basic informations."
    },
    5: {
        title: "Teaching Informations",
        subtitle: "Tell us about your teaching language, which subjects you teach, which grades you teach, how you teach, and how much you charge."
    },
    6: {
        title: "Teaching Experiences & Qualifications",
        subtitle: "Tell us about your teaching experiences & what qualifications you have.",
        regularSubtitle: "Upload your relevant documents for be verified. All documents must be clear, legible, and in PDF or JPG format (max 5MB each)."
    },
    7: {
        title: "ID verification",
        subtitle: "Upload relevant document and select method you like to verify your identity",
        regularSubtitle: "All documents must be clear, legible, and in PDF or JPG format (max 5MB each)"
    },
    8: {
        title: "Preview application",
        subtitle: "Review your application before submission",
        regularSubtitle: "If you want change anything in application you can go back change, then submit the application."
    },
    9: {
        title: "Your profile is ready!",
        subtitle: "Our verification process takes 5-6 working days to verify your tutor profile.",
        regularSubtitle: "Stay connected! We will email you to schedule your video verification."
    },
}
const stepperSteps = [
    { stepNumber: 1, label: "Step 01" },
    { stepNumber: 2, label: "Step 02" },
    { stepNumber: 3, label: "Step 03" },
    { stepNumber: 4, label: "Step 04" },
    { stepNumber: 5, label: "Step 05" }
];
const getStepperStep = (currentStep) => {
    const stepMap = {
        4: 1,
        5: 2,
        6: 3,
        7: 4,
        8: 5
    };
    return stepMap[currentStep] || 1;
};

const getOnboardingStep = (user) => {
    if (user.verificationRequested) return 9;
    if (!user.firstName || !user.lastName || !user.NIC || !user.dateOfBirth || !user.mobileNumber || !user.address || !user.gender) return 3;
    if (!user.teachingLevels?.length || !user.tutorType || !user.classMode || !user.classLocation || !user.teachingLanguage?.length || !user.teachingAvailability?.length || !user.teachingLevels?.some(tl => tl.individualClass?.available || tl.groupClass?.available)) return 4;
    if (!user.experience?.length || !user.highestEducation?.education) return 5;
    if (!user.professionalPhotoURL || !user.NICURL) return 6;
    if (!user.verificationRequested) return 7;
    return 2;
};

onMounted(async () => {
    if (authStore.user) {
        alreadyLoggedIn.value = true;
        try {
            // Sponsored flag is true from the very first login — fetch it before picking a step.
            const [response] = await Promise.all([getProfile(), subscriptionStore.fetchMy()]);
            const freshUser = response.data.data;
            authStore.user = { email: authStore.user.email, ...freshUser };
            localData.value = { ...authStore.user };

            if (props.registrationData) {
                step.value = 2;
            } else {
                step.value = getOnboardingStep(freshUser);
            }

            // Sponsored members never land on the package step (3) — send them past it.
            if (managedByInstitute.value && step.value === 3) {
                step.value = 4;
            }

            if (freshUser.verificationRequested) {
                notifyContrast("Verification Pending!", "Hi " + freshUser.username +
                    "! We are still going through your verification process. Please check your email for further updates.");
                return
            }
            notifyContrast("Complete Your Onboarding", "Hi " + freshUser.username +
                "! It looks like you didn’t finish setting up your profile. To get the best learning experience, please complete your onboarding process.");
        } catch (error) {
            notifyContrast("Error", "Could not fetch your profile. Please try refreshing the page.");
        }
    }
})

watch(
    () => props.registrationData,
    (newVal) => {
        localData.value = newVal;
    }
);

const setRegistrationData = (data) => {
    localData.value = data;
    step.value = 2;
    alreadyLoggedIn.value = true;
}

const setGetStarted = () => {
    // Skip the package selection step (3) for institute-sponsored tutors.
    step.value = managedByInstitute.value ? 4 : 3;
}

const setPackageInfo = () => {
    step.value = 4;
}

const setPersonalInfo = async (data) => {
    try {
        await submitData(data)
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 5;
}

const setTeachingInfo = async (data) => {
    try {
        await submitData(data)
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 6;
}

const setExperianceInfo = async (data) => {
    try {
        await submitData(data)
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 7;
}

const setVerificationInfo = async (data) => {
    try {
        await submitData(data)
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 8;
}

const doVerification = async (consentData = {}) => {
    try {
        await submitData({
            verifiedByAdmins: false,
            verificationRequested: true,
            registrationCompleted: true,
            informationAccurate: consentData.informationAccurate ?? true,
            backgroundVerify: consentData.backgroundVerify ?? true,
        });
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
    step.value = 9;
}

const bypassVerification = async () => {
    try {
        await submitData({ verifiedByAdmins: true, verificationRequested: true, registrationCompleted: true });
        notifyContrast("Congratulations!", "Your account has been verified successfully.");
        router.push('/tutor_dashboard');
    } catch (error) {
        notifyContrast("Error", error.message || "Failed to update profile. Please try again.");
        return;
    }
}
const submitData = async (data) => {
    try {
        await updateProfile(data);
        authStore.user = { ...authStore.user, ...data };
    } catch (error) {
        throw new Error("Failed to update profile");
    }
}

const previousStep = () => {
    // Step 4 normally goes back to the package step (3); sponsored tutors skip it back to 2.
    if (managedByInstitute.value && step.value === 4) {
        step.value = 2;
        return;
    }
    if (step.value > 1) {
        step.value -= 1;
    }
};
const currentImage = computed(() => {
    switch (step.value) {
        case 1: return step1Img;
        case 2: return step2Img;
        default: return "";
    }
});
const layoutType = computed(() => {
    switch (step.value) {
        case 3: return 'full';
        case 4: return 'full';
        case 5: return 'full';
        case 6: return 'full';
        case 7: return 'full';
        case 8: return 'full';
        case 9: return 'full';
        default: return "";
    }
});
</script>

<style scoped>
.motivation-card {
    width: 100%;
    border: none;
    box-shadow: none;
    max-width: 700px; /* keep regular steps contained */
}
</style>